import { createSelector } from 'reselect'
import { isLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { OptimizelyExperiments } from 'constants/optimizely'
import { isSpoofed } from 'selectors/featuresSelectors'
import { isE2EUser } from 'lib/checkout/form'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'

// Experiment variants
export const FLASH_PAYMENT_SCHEDULE_VARIANT_CONTROL = 'control'
export const FLASH_PAYMENT_SCHEDULE_VARIANT_MIN_1999 = 'min_1999'
export const FLASH_PAYMENT_SCHEDULE_VARIANT_MIN_2999 = 'min_2999'

// Template IDs for each variant
export const FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999 = '001_hotel_50_percentage_dynamic_1999_AUD'
export const FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_2999 = '001_hotel_50_percentage_dynamic_2999_AUD'

// Minimum flash hotel offer value to trigger the experiment (AUD only)
const FLASH_PAYMENT_SCHEDULE_MINIMUM_OFFER_VALUE = 1999

/**
 * Gets the experiment variation key directly from state.
 * Unlike getOptimizelyExperimentVariation, this returns the variationKey even when enabled=false,
 * which is the case for control/baseline variants in Optimizely.
 *
 * Returns the variationKey if the experiment has been triggered, undefined otherwise.
 */
const getFlashPaymentScheduleExperimentVariationKey = createSelector(
  (state: App.State) => state.system.features.experiments[OptimizelyExperiments.paymentFlashPaymentSchedules],
  (experiment): string | undefined => {
    // Check if experiment has been triggered - this distinguishes between
    // "user in experiment (control)" vs "user not in experiment at all"
    if (!experiment?.triggered) {
      return undefined
    }
    return experiment.variationKey
  },
)

const isFlashHotelTotalAboveThreshold = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (items, offers, accommodationBreakdownView, useMemberPrice): boolean => {
    const leHotelItems = items.filter(isLEHotelItem)

    const flashHotelTotal = leHotelItems.reduce((sum, item) => {
      const offer = offers[item.offerId]
      if (!isFlashHotelOffer(offer)) {
        return sum
      }

      const hotelBreakdownView = accommodationBreakdownView.data.accommodations.find(
        view => view.items.some(itemView => itemView.itemId === item.itemId),
      )
      const hotelItemView = hotelBreakdownView?.items.find(itemView => itemView.itemId === item.itemId)
      const price = (useMemberPrice && hotelItemView?.memberPrice) ? hotelItemView?.memberPrice : hotelItemView?.price ?? 0
      const total = price + (hotelItemView?.taxesAndFees ?? 0)

      return sum + total
    }, 0)

    return flashHotelTotal >= FLASH_PAYMENT_SCHEDULE_MINIMUM_OFFER_VALUE
  },
)

/**
 * Checks if the experiment should be triggered based on:
 * - User is not spoofed (spoofed users are excluded but get min_1999 template)
 * - User is not an E2E test user
 * - Total of all flash hotel items >= 1999 AUD
 * - Currency is AUD
 */
export const shouldTriggerFlashPaymentScheduleExperiment = createSelector(
  isFlashHotelTotalAboveThreshold,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.auth.account.email,
  (flashHotelTotalAboveThreshold, currency, userIsSpoofed, userEmail): boolean => {
    if (userIsSpoofed) {
      return false
    }

    if (isE2EUser(userEmail ?? '')) {
      return false
    }

    if (currency !== 'AUD') {
      return false
    }

    if (!flashHotelTotalAboveThreshold) {
      return false
    }

    return true
  },
)

/**
 * Gets the experiment variant if the experiment conditions are met and the user is in the experiment.
 * Returns undefined if conditions aren't met or user is not in the experiment.
 */
export const getFlashPaymentScheduleExperimentVariant = createSelector(
  shouldTriggerFlashPaymentScheduleExperiment,
  getFlashPaymentScheduleExperimentVariationKey,
  (shouldTrigger, variationKey): string | undefined => {
    if (!shouldTrigger) {
      return undefined
    }
    return variationKey
  },
)

/**
 * Returns true if payment schedules should be disabled due to the experiment "control" variant.
 *
 * Important distinction:
 * - Experiment conditions NOT met → returns false (payment schedules work normally)
 * - Experiment conditions met, user NOT in experiment → returns false (payment schedules work normally)
 * - Experiment conditions met, user in experiment with "control" variant → returns true (payment schedules disabled)
 * - Experiment conditions met, user in experiment with other variant → returns false (template override applied)
 */
export const shouldDisablePaymentSchedulesForFlashExperiment = createSelector(
  shouldTriggerFlashPaymentScheduleExperiment,
  getFlashPaymentScheduleExperimentVariationKey,
  (shouldTrigger, variationKey): boolean => {
    // Only disable if experiment conditions are met AND user is in the "control" variant
    return shouldTrigger && variationKey === FLASH_PAYMENT_SCHEDULE_VARIANT_CONTROL
  },
)

/**
 * Checks if spoofed user meets the base conditions for template override (excluding spoof check).
 * Used to determine if a spoofed user should get the min_1999 template.
 */
const meetsBaseConditionsForTemplateOverride = createSelector(
  isFlashHotelTotalAboveThreshold,
  (state: App.State) => state.geo.currentCurrency,
  (flashHotelTotalAboveThreshold, currency): boolean => {
    return currency === 'AUD' && flashHotelTotalAboveThreshold
  },
)

/**
 * Gets the template ID override based on the experiment variant.
 * Returns undefined if no override should be applied.
 *
 * Important distinction:
 * - Payment schedule rebooking active → returns undefined (use original order's template)
 * - Spoofed user meeting base conditions → returns min_1999 template (bypasses experiment)
 * - Experiment conditions NOT met → returns undefined (use offer's template)
 * - Experiment conditions met, user NOT in experiment → returns undefined (use offer's template)
 * - Experiment conditions met, user in "control" variant → returns undefined (payment schedules disabled)
 * - Experiment conditions met, user in "min_1999" or "min_2999" → returns override template
 */
export const getFlashPaymentScheduleTemplateOverride = createSelector(
  (state: App.State) => isSpoofed(state),
  meetsBaseConditionsForTemplateOverride,
  shouldTriggerFlashPaymentScheduleExperiment,
  getFlashPaymentScheduleExperimentVariationKey,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingId,
  (userIsSpoofed, meetsBaseConditions, shouldTrigger, variationKey, paymentScheduleRebookingId): string | undefined => {
    // Skip template override when rebooking - respect the original order's schedule type
    if (paymentScheduleRebookingId) {
      return undefined
    }

    // Spoofed users always get min_1999 template if they meet base conditions
    if (userIsSpoofed && meetsBaseConditions) {
      return FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999
    }

    // Only override if experiment conditions are met AND variant has a template
    if (!shouldTrigger) {
      return undefined
    }

    switch (variationKey) {
      case FLASH_PAYMENT_SCHEDULE_VARIANT_MIN_1999:
        return FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999
      case FLASH_PAYMENT_SCHEDULE_VARIANT_MIN_2999:
        return FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_2999
      default:
        return undefined
    }
  },
)
